import React, { useContext, useEffect, useRef, useState } from 'react'
import { cents } from '../../util/formatters'
import { BidHistoryTooltipSuppressionContext } from '../bid_history_tooltip_suppression'
import { Popover } from '../ui/popover'

interface InlineEditPopoverProps {
    value: number | null | undefined
    fallback?: number | null | undefined
    label: string
    onSave: (cents: number | null) => Promise<unknown>
    disabled?: boolean
    inherited?: boolean
    allowClear?: boolean
}

const Spinner = () => (
    <svg
        className="h-3.5 w-3.5 animate-spin text-white"
        viewBox="0 0 24 24"
        fill="none"
        aria-label="Saving"
    >
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
        <path
            d="M21 12a9 9 0 0 0-9-9"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
        />
    </svg>
)

export const InlineEditPopover = ({
    value,
    fallback,
    label,
    onSave,
    disabled = false,
    inherited = false,
    allowClear = false,
}: InlineEditPopoverProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [draft, setDraft] = useState('')
    const [saving, setSaving] = useState(false)
    const savingRef = useRef(saving)
    const setBidHistoryTooltipSuppressed = useContext(BidHistoryTooltipSuppressionContext)

    useEffect(() => {
        savingRef.current = saving
    }, [saving])

    useEffect(() => {
        setBidHistoryTooltipSuppressed?.(isOpen)
        return () => setBidHistoryTooltipSuppressed?.(false)
    }, [isOpen, setBidHistoryTooltipSuppressed])

    const inheritedDisplay = inherited && fallback != null ? `Inherited: ${cents(fallback)}` : null
    const inheritedValue = inheritedDisplay != null
    const display = value != null ? cents(value) : (inheritedDisplay ?? '—')
    const clear = allowClear && draft.trim() === '' && value != null
    const next = clear ? null : Math.round(Number.parseFloat(draft) * 100)
    const valid = clear || (next !== null && Number.isFinite(next))

    const open = () => {
        const seed = value ?? fallback
        setDraft(seed == null ? '' : (seed / 100).toFixed(2))
        setIsOpen(true)
    }

    const close = () => {
        if (!savingRef.current) {
            setIsOpen(false)
        }
    }

    const save = async () => {
        if (saving || !valid) {
            return
        }
        if (next === value) {
            setIsOpen(false)
            return
        }
        setSaving(true)
        try {
            await onSave(next)
            setIsOpen(false)
        } catch {
            // Keep the draft available for a retry after a request failure.
        } finally {
            setSaving(false)
        }
    }

    const panel = (
        <div className="w-60 rounded-md border border-gray-200 bg-white p-3 shadow-lg">
            <div className="mb-1.5 text-[12px] font-medium text-gray-700">{label}</div>
            <div className="relative h-7">
                <span className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-gray-500">
                    $
                </span>
                <input
                    autoFocus
                    aria-label={label}
                    inputMode="decimal"
                    value={draft}
                    disabled={saving}
                    onChange={(event) => setDraft(event.target.value)}
                    onFocus={(event) => event.target.select()}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            void save()
                        }
                    }}
                    className="box-border h-7 w-full rounded border border-blue-500 bg-white pl-4 pr-2 tabular-nums text-gray-900 shadow-[0_0_0_2px_rgba(59,130,246,0.15)] outline-none disabled:bg-gray-50"
                />
            </div>
            <div className="mt-3 flex items-center gap-2">
                <button
                    type="button"
                    disabled={saving}
                    onClick={close}
                    className="ml-auto rounded border border-gray-300 bg-white px-2.5 py-1 text-[13px] hover:bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={saving || !valid}
                    onClick={() => void save()}
                    style={{
                        width: 84,
                        opacity: !valid ? 0.45 : 1,
                        cursor: !valid ? 'not-allowed' : 'pointer',
                    }}
                    className="inline-flex items-center justify-center gap-1.5 rounded border-0 bg-blue-600 py-1 text-[13px] text-white hover:bg-blue-700"
                >
                    {saving && <Spinner />}
                    {saving ? 'Saving…' : 'Save'}
                </button>
            </div>
        </div>
    )

    if (disabled) {
        return (
            <span className={`tabular-nums ${inheritedValue ? 'text-gray-400' : 'text-gray-900'}`}>
                {display}
            </span>
        )
    }

    return (
        <Popover
            content={panel}
            interactionKind="click"
            placement="bottom"
            isOpen={isOpen}
            onOpening={open}
            onClose={close}
        >
            <button
                type="button"
                disabled={saving}
                title={
                    inheritedValue
                        ? `Click to set ${label.toLowerCase()} for this row`
                        : `Click to change this ${label.toLowerCase()}`
                }
                className={`h-7 cursor-pointer rounded border-0 px-1.5 text-left tabular-nums hover:bg-gray-100 ${
                    inheritedValue ? 'text-gray-400' : 'text-gray-900'
                }`}
            >
                {display}
            </button>
        </Popover>
    )
}
